import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';

interface Message {
  id: number;
  sender: string;
  message: string;
  time: string;
  unread: boolean;
}

interface MessagesPopoverProps {
  className?: string;
}

export const MessagesPopover: React.FC<MessagesPopoverProps> = ({ className }) => {
  const [showMessages, setShowMessages] = useState(false);
  const { toast } = useToast();

  // Mock messages data
  const messages: Message[] = [
    { id: 1, sender: 'Anna Schmid', message: 'Can you review the latest report?', time: '10m ago', unread: true },
    { id: 2, sender: 'Marc Müller', message: 'Uploaded new documents', time: '1h ago', unread: false },
  ];

  const unreadCount = messages.filter(m => m.unread).length;

  return (
    <Popover open={showMessages} onOpenChange={setShowMessages}>
      <PopoverTrigger asChild>
        <button 
          className={`w-[38px] h-[38px] relative bg-brand-secondary rounded-full flex items-center justify-center hover:bg-brand-accent/10 transition-colors ${className}`}
          aria-label="Messages"
        >
          <i className="fa-solid fa-message text-brand-primary text-sm"></i>
          {unreadCount > 0 && (
            <div className="absolute top-1 right-1 w-2 h-2 bg-brand-danger rounded-full border-2 border-brand-secondary"></div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Messages</h3>
          <Badge variant="secondary">{unreadCount} new</Badge>
        </div>
        <ScrollArea className="h-80">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`p-4 border-b hover:bg-accent cursor-pointer transition-colors ${
                msg.unread ? 'bg-accent/50' : ''
              }`}
              onClick={() => {
                toast({
                  title: "Message opened",
                  description: `Viewing message from ${msg.sender}`,
                });
                setShowMessages(false);
              }}
            >
              <div className="flex items-start gap-3">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-brand-primary text-brand-primary-foreground text-xs">
                    {msg.sender.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium truncate">{msg.sender}</p>
                    <span className="text-xs text-muted-foreground">{msg.time}</span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{msg.message}</p>
                </div>
              </div>
            </div>
          ))}
        </ScrollArea>
        <div className="p-2 border-t">
          <Button variant="ghost" className="w-full" size="sm">
            View all messages
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};







